<template>
  <a-form-model
    class="attribute-list-record"
    :labelCol="{ span: 0, offset: 0 }"
    :wrapperCol="{ span: 24, offset: 0 }"
    :colon="false"
  >
    <slot name="name">
      <div class="attribute-list-record__name">
        <span
          v-if="props.attribute.is_required"
          class="attribute-list-record__required"
          aria-hidden="true"
        >
          *
        </span>
        {{ getAttributeLabel(props.attribute) }}
      </div>
    </slot>

    <a-form-model-item
      class="attribute-list-record__value"
      :validateStatus="validateStatus"
      :help="fieldValidationMessage"
    >
      <InfiniteScrollSelect
        ref="attributeValueSelectRef"
        :value="selectedValue"
        :mode="props.attribute.allow_multiple ? 'multiple' : 'default'"
        :placeholder="`${$t('offers.create_form_placeholders.choose_value')}`"
        :customRequest="fetchAttributeValues"
        :customRequestParams="{ code: props.attribute.code }"
        :defaultOptions="props.attribute.value"
        onFocusFetch
        searchKey="value_str"
        valueKey="code"
        @change="onValueChanged"
      >
        <template #extraButton>
          <a-button
            type="link"
            block
            icon="plus"
            style="padding: 0 8px; text-align: left"
            @click="openCreateValueModal"
          >
            {{ $t("offers.create_new_attribute_value") }}
          </a-button>
        </template>

        <template #option="{ option }">
          {{ getAttributeValueLabel(option) }}

          <template v-if="props.attribute.value_type === 'NUMERIC'">
            {{ option?.unit?.name }}
          </template>
        </template>
      </InfiniteScrollSelect>
    </a-form-model-item>

    <a-form-model-item
      v-if="showDeleteButton"
      class="attribute-list-record__action"
    >
      <a-button
        icon="delete"
        type="dashed"
        shape="circle"
        :aria-label="$t('common.delete')"
        @click="removeAttribute"
      />
    </a-form-model-item>
    <CreateAttributeValueModal
      :visible="showCreateValueModal"
      :attributeCode="props.attribute.code"
      :attributeValueType="props.attribute.value_type"
      @close="showCreateValueModal = false"
      @submit="selectCreatedValue"
    />
  </a-form-model>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"
import { TranslateResult } from "vue-i18n"

import InfiniteScrollSelect from "@/components/Common/InfiniteScrollSelect/index.vue"
import i18n from "@/i18n"
import { fetchAttributeValues } from "@/modules/offers/api/productModeration"
import type { AttributeValueSelectHandle } from "@/modules/offers/types/createOffer"
import {
  ProductModerationAttribute,
  ProductModerationAttributeValue
} from "@/modules/offers/types/offers"

import { getAttributeLabel, getAttributeValueLabel } from "../../utils/attributeLabels"
import { hasAttributeValue } from "../../utils/productAttributes"

import CreateAttributeValueModal from "./CreateAttributeValueModal.vue"

type Props = {
  attribute: ProductModerationAttribute
  showDeleteButton?: boolean
  showValidation?: boolean
}
type Emits = {
  (event: "remove" | "change", attribute: ProductModerationAttribute): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const selectedValue = computed(() => {
  const codes = props.attribute.value.map((value) => value.code)
  return props.attribute.allow_multiple ? codes : codes[0]
})

const attributeValueSelectRef = ref<AttributeValueSelectHandle>()

const validateStatus = computed<string>(() => {
  if (!props.attribute.is_required) return "success"

  if (!props.showValidation) return "success"

  return hasAttributeValue(props.attribute) ? "success" : "error"
})
const fieldValidationMessage = computed<TranslateResult>(() =>
  validateStatus.value === "error" ? i18n.t("validations.required_field") : ""
)

const removeAttribute = () => {
  emit("remove", props.attribute)
}
const findValueOption = (item: string): ProductModerationAttributeValue => {
  return (
    attributeValueSelectRef.value?.selectOptions?.find(
      (option: ProductModerationAttributeValue) => option.code === item
    ) || props.attribute.value?.find((option) => option?.code === item)
  )
}
const onValueChanged = (value: string | string[]) => {
  const codes = Array.isArray(value) ? value : value ? [value] : []
  emit("change", {
    ...props.attribute,
    value: codes.map(findValueOption).filter(Boolean)
  })
}

const showCreateValueModal = ref(false)

const openCreateValueModal = () => {
  showCreateValueModal.value = true
}
const selectCreatedValue = (value: ProductModerationAttributeValue) => {
  if (props.attribute.value.some(({ code }) => code === value.code)) return

  emit("change", {
    ...props.attribute,
    value: props.attribute.allow_multiple ? [...props.attribute.value, value] : [value]
  })
}
</script>

<style lang="scss" scoped>
.attribute-list-record {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 40px;
  padding: 4px 0;

  &__name {
    flex: 0 1 440px;
    min-width: 0;
    padding-left: 8px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }

  &__required {
    margin-right: 4px;
    color: $red-color-5;
  }

  &__value {
    flex: 0 1 285px;
    min-width: 0;
    margin: 0;
  }

  &__action {
    flex: 0 0 32px;
    margin: 0;
  }

  ::v-deep .ant-form-item-control {
    line-height: 32px;
  }

  ::v-deep .ant-form-item-control-wrapper {
    width: 100%;
  }

  ::v-deep .ant-form-item-children {
    display: flex;
    align-items: flex-start;
  }
}
</style>
